import { FileIdUpload, InputFile, InputFileId } from "@scrile/api-provider/dist/api/FileProvider";
import { UploadProgress } from "@scrile/tools/dist/lib/FileUploader";
import providers from "@/lib/api";
import { getFileMimeType } from "@/lib/fileHelpers";
import FileUploader, { showDefaultUploadError } from "@/lib/FileUploader";
import logger from "@/modules/logger";

export const ABORT_UPLOADING_NAME = "AbortError";

export type UploadFilesData = {
  file: File;
  fileIdUpload: FileIdUpload;
  onSuccess?: (uid: string) => Promise<void> | void;
  onError?: (e: Error, uid: string) => void;
  onAbortUploading?: (uid: string) => void;
  onUploadProgress?: (p: UploadProgress) => void;
};

export default function useFiles() {
  const findFiles = ({ fileIds }: { fileIds: InputFileId[] }) => providers.FileProvider.find({ fileIds });
  const updateFile = ({ data }: { data: InputFile }) => providers.FileProvider.update({ data });
  const deleteFile = ({ fileId }: { fileId: InputFileId }) => providers.FileProvider.delete({ data: fileId });
  const stopFileUploading = (fileId: string): void => FileUploader.removeFromQueue(fileId);
  const getUploadUrl = async (fileIds: InputFileId[]): Promise<FileIdUpload[]> => {
    return providers.FileProvider.getUploadUrl({ fileIds });
  };

  const uploadFilesToS3 = (uploadData: UploadFilesData[]): void => {
    FileUploader.addToQueue(
      uploadData.map(data => ({
        id: data.fileIdUpload.fileUpload.uuid,
        file: data.file,
        urlPart: data.fileIdUpload.fileUpload.urlPart,
        fileType: getFileMimeType(data.file),
        onUploadProgress: data.onUploadProgress,
        onSuccess: async () => {
          data.onSuccess && (await data.onSuccess(data.fileIdUpload.fileUpload.uuid));
        },
        onError: (e: Error) => {
          if (e.name === ABORT_UPLOADING_NAME) {
            data.onAbortUploading && data.onAbortUploading(data.fileIdUpload.fileUpload.uuid);
          } else {
            showDefaultUploadError();
            logger.debug("File uploading error", {
              dataId: data.fileIdUpload.fileUpload.uuid,
              dataPartUrl: data.fileIdUpload.fileUpload.urlPart,
              error: e,
            });
            data.onError && data.onError(e, data.fileIdUpload.fileUpload.uuid);
          }
        },
      }))
    );
  };

  const uploadThenUpdate = async ({
    fileId,
    file,
    onUploadProgress,
    onSuccessCb,
  }: {
    fileId: InputFileId;
    file: File;
    onUploadProgress?: (p: UploadProgress) => void;
    onSuccessCb?: () => Promise<void> | void;
  }) => {
    const fileIdUpload = await getUploadUrl([fileId]);
    await new Promise<void>(resolve => {
      uploadFilesToS3([
        {
          file,
          fileIdUpload: fileIdUpload[0],
          onSuccess: () => {
            resolve();
            onSuccessCb && onSuccessCb();
          },
          onUploadProgress,
        },
      ]);
    });

    return await updateFile({ data: { fileId: fileIdUpload[0].fileId, uuid: fileIdUpload[0].fileUpload.uuid } });
  };

  return {
    findFiles,
    updateFile,
    deleteFile,
    uploadFilesToS3,
    uploadThenUpdate,
    stopFileUploading,
    getUploadUrl,
  };
}
